import { useCallback, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";
import { abcAzimuth } from "@nav-games/astro";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface AbcDrillProps {
  onNavigate: (to: string) => void;
}

const MODULE = "astro";
const GAME = "abc-azimuth";
const CARDS_PER_SESSION = 10;
const CHOICES_PER_CARD = 4;

interface Card {
  latDeg: number;
  decDeg: number;
  lhaDeg: number;
  answerZn: number;
  choices: readonly number[];
}

function randInt(lo: number, hi: number): number {
  return lo + Math.floor(Math.random() * (hi - lo + 1));
}
function randSign(): number {
  return Math.random() < 0.5 ? -1 : 1;
}

function buildCard(): Card {
  // Latitude in 10°–60°, hemisphere random.
  const latDeg = randSign() * randInt(10, 60);
  // Dec in 0°–55°, signed.
  const decDeg = randSign() * randInt(0, 55);
  // LHA across the full circle but biased away from the meridian
  // (avoid LHA exactly = 0 / 180 where the ABC formulas hit
  // singularities the student doesn't need to learn about yet).
  let lha = randInt(5, 355);
  if (lha === 180) lha = 175;
  if (lha === 360) lha = 5;

  const result = abcAzimuth({ latDeg, decDeg, lhaDeg: lha });
  const answerZn = Math.round(result.znDeg);

  // Distractor candidates — the canonical mistakes.
  const candidates: number[] = [
    (answerZn + 180) % 360, // forgot the E/W rule
    (360 - answerZn) % 360, // wrong sign on C / wrong quadrant
    180 - answerZn < 0 ? 540 - answerZn : 180 - answerZn, // wrong quadrant
  ];
  const distractors: number[] = [];
  const seen = new Set<number>([answerZn]);
  for (const c of candidates) {
    const cn = ((Math.round(c) % 360) + 360) % 360;
    if (!seen.has(cn) && Math.abs(cn - answerZn) > 5) {
      seen.add(cn);
      distractors.push(cn);
    }
    if (distractors.length >= CHOICES_PER_CARD - 1) break;
  }
  let off = 30;
  while (distractors.length < CHOICES_PER_CARD - 1) {
    for (const sign of [+1, -1]) {
      const cn = ((Math.round(answerZn + sign * off) % 360) + 360) % 360;
      if (!seen.has(cn) && Math.abs(cn - answerZn) > 5) {
        seen.add(cn);
        distractors.push(cn);
      }
      if (distractors.length >= CHOICES_PER_CARD - 1) break;
    }
    off += 25;
  }

  const choices = [answerZn, ...distractors];
  for (let i = choices.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = choices[i]!;
    choices[i] = choices[j]!;
    choices[j] = a;
  }
  return { latDeg, decDeg, lhaDeg: lha, answerZn, choices };
}

function fmtZn(deg: number): string {
  return `${String(((deg % 360) + 360) % 360).padStart(3, "0")}° T`;
}
function fmtLat(deg: number): string {
  return `${Math.abs(deg).toFixed(0)}°${deg >= 0 ? "N" : "S"}`;
}
function fmtDec(deg: number): string {
  return `${deg >= 0 ? "N" : "S"} ${Math.abs(deg).toFixed(0)}°`;
}

export function AbcDrill({ onNavigate }: AbcDrillProps) {
  const recorder = useMemo(
    () =>
      new SessionRecorder({
        module: MODULE,
        game: GAME,
        length: CARDS_PER_SESSION,
      }),
    [],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => buildCard());
  const [shownAt, setShownAt] = useState(() => Date.now());
  const [chosen, setChosen] = useState<number | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setCard(buildCard());
    setShownAt(Date.now());
    setChosen(null);
  }, [index, recorder]);

  const onChoose = useCallback(
    (choice: number) => {
      if (chosen !== null || result) return;
      const ok = choice === card.answerZn;
      const attempt: Omit<Attempt, "module"> = {
        char: "abc",
        ok,
        ms: Date.now() - shownAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(choice);
    },
    [card, chosen, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          setIndex(0);
          setCard(buildCard());
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/play/astro")}
      />
    );
  }

  const revealed = chosen !== null;
  const abc = abcAzimuth({
    latDeg: card.latDeg,
    decDeg: card.decDeg,
    lhaDeg: card.lhaDeg,
  });

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: 8,
        }}
      >
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel
        section={helpOpen ? "astro" : null}
        onClose={() => setHelpOpen(false)}
      />

      <p className="small muted">
        Question {index + 1} of {CARDS_PER_SESSION}
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Azimuth — ABC method</h3>
        <p style={{ marginBottom: 8 }}>
          You have computed an LHA for a body. Using the ABC method, work
          out the body's true azimuth Zn.
        </p>
        <div
          className="small"
          style={{
            padding: 10,
            marginBottom: 12,
            background: "var(--bg-elev)",
            border: "1px solid var(--border)",
            borderRadius: "var(--radius-sm)",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          }}
        >
          <div>Latitude (Lat): <strong>{fmtLat(card.latDeg)}</strong></div>
          <div>Declination (Dec): <strong>{fmtDec(card.decDeg)}</strong></div>
          <div>LHA: <strong>{card.lhaDeg}°</strong></div>
        </div>

        <div
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}
        >
          {card.choices.map((c) => {
            const isCorrect = c === card.answerZn;
            const isChosen = c === chosen;
            return (
              <button
                key={c}
                className="secondary"
                disabled={revealed}
                onClick={() => onChoose(c)}
                style={{
                  fontSize: 18,
                  padding: "14px 0",
                  fontWeight: 600,
                  background:
                    revealed && isCorrect
                      ? "rgba(44, 122, 59, 0.18)"
                      : revealed && isChosen
                        ? "rgba(184, 48, 42, 0.18)"
                        : undefined,
                }}
              >
                {fmtZn(c)}
              </button>
            );
          })}
        </div>

        {revealed && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 6,
              background:
                chosen === card.answerZn
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {chosen === card.answerZn
                ? `✓ Correct — Zn = ${fmtZn(card.answerZn)}.`
                : `✗ Not quite — Zn = ${fmtZn(card.answerZn)}.`}
            </p>
            <div
              className="small"
              style={{
                marginTop: 8,
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                whiteSpace: "pre-wrap",
              }}
            >
{`A = tan(Lat)/tan(LHA) = ${abc.a.toFixed(4)}  named ${abc.aName}
B = tan(Dec)/sin(LHA) = ${abc.b.toFixed(4)}  named ${abc.bName}
C = A ${abc.aName === abc.bName ? "+" : "−"} B = ${abc.c.toFixed(4)}  named ${abc.cName}
Z = arctan( 1 / (C · cos Lat) ) = ${abc.zDeg.toFixed(1)}°  named ${abc.zNS}${abc.zEW}
Zn = ${abc.znDeg.toFixed(1)}° T`}
            </div>
            <button onClick={advance} style={{ marginTop: 12, width: "100%" }}>
              {index + 1 >= CARDS_PER_SESSION ? "See score →" : "Next →"}
            </button>
          </div>
        )}
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/astro")}>
        ← Back to Astronavigation
      </button>
    </>
  );
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      <div
        className="row"
        style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}
      >
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Astronavigation
        </button>
      </div>
    </>
  );
}
